import "./ScoutingMarker.css";

import { boolStringToInt, imageFetcher } from "../../services/utils";
import { useDispatch, useSelector } from "react-redux";
import { useMemo, useState } from "react";

import { Modal } from "reactstrap";
import PropTypes from "prop-types";
import { PropertiesMasonry } from "../../components/PropertiesMasonry/PropertiesMasonry";
import { SCOUTING_COLOR } from "../../constants";
import { format } from "date-fns";
import { selectScoutingColorScale } from "../../selectors/resultMap";
import { setSelectedScoutingFeature } from "../../actions/resultMap";
import useSWRImmutable from "swr/immutable";

const ScoutingImage = ({ feature }) => {
  const user = useSelector((state) => state.user);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { data, isLoading } = useSWRImmutable(
    `/api/client/scouting_features/${feature.id}/sample_image`,
    imageFetcher(user)
  );
  const [selectedScoutingTrait, scoutingTraits] = useSelector(
    ({ resultMap }) => [
      resultMap.selectedScoutingTrait,
      resultMap.scoutingTraits,
    ]
  );

  const scoutingProperties = useMemo(() => {
    return Object.keys(feature.meta).reduce((acc, trait) => {
      acc[trait] = feature.meta[trait]?.value;
      return acc;
    }, {});
  }, [feature]);

  return (
    data && (
      <>
        <Modal
          className="plot-modal"
          isOpen={modalIsOpen}
          toggle={() => setModalIsOpen(false)}
          centered
        >
          {!isLoading && <img src={data.url} alt="scouting" />}
        </Modal>
        <img
          className="scouting-image"
          src={data.url}
          alt="scouting"
          onClick={() => setModalIsOpen(true)}
        />
        {Object.keys(scoutingTraits).length > 0 && (
          <PropertiesMasonry
            properties={scoutingProperties}
            highlightedProperty={selectedScoutingTrait.key}
          />
        )}
      </>
    )
  );
};

ScoutingImage.propTypes = {
  feature: PropTypes.object.isRequired,
};

export const ScoutingMarker = ({ feature }) => {
  const dispatch = useDispatch();
  const [selectedScoutingFeature, selectedScoutingTrait] = useSelector(
    ({ resultMap }) => [
      resultMap.selectedScoutingFeature,
      resultMap.selectedScoutingTrait,
    ]
  );
  const scoutColorScale = useSelector(selectScoutingColorScale);
  const isOpen = selectedScoutingFeature?.id === feature.id;

  const markerColor = useMemo(() => {
    if (!selectedScoutingTrait) return SCOUTING_COLOR;

    const value = feature.meta?.[selectedScoutingTrait.key]?.value;
    if (!value) return SCOUTING_COLOR;

    const color = scoutColorScale.func(boolStringToInt(value));
    return color === "rgba(255, 255, 255, 0)" ? SCOUTING_COLOR : color;
  }, [selectedScoutingTrait, feature, scoutColorScale]);

  const handleCloseMarker = () => {
    dispatch(setSelectedScoutingFeature(null));
  };

  return (
    <div className={`scouting-marker ${isOpen ? "active" : ""}`}>
      {isOpen && (
        <div className="scouting-bubble p-2">
          <span className="pb-2 d-flex gap-2 align-items-center">
            <div
              className="scouting-marker-icon"
              style={{ backgroundColor: markerColor }}
            >
              <i className="fa-solid fa-camera" />
            </div>
            <span className="font-weight-bold">
              {format(feature.datetime, "yyyy-MM-dd")}
            </span>
            <span>{format(feature.datetime, "hh:mm:ss")}</span>
            <div className="flex-grow-1" />
            <i
              className="fa-solid fa-xmark close-icon"
              onClick={handleCloseMarker}
            />
          </span>
          <ScoutingImage feature={feature} />
        </div>
      )}
      <div
        className="scouting-marker-icon"
        style={{ backgroundColor: markerColor }}
        onClick={() => dispatch(setSelectedScoutingFeature(feature))}
      >
        <i className="fa-solid fa-camera" />
      </div>
    </div>
  );
};

ScoutingMarker.propTypes = {
  feature: PropTypes.shape({
    id: PropTypes.string.isRequired,
    datetime: PropTypes.string.isRequired,
  }).isRequired,
};
